/**
 * LoginForm Component
 *
 * Handles user login with direct API calls (no Redux middleware).
 * Replicates Angular's exact login flow:
 * 1. Call login API → get token
 * 2. Store token in sessionStorage
 * 3. Call getUserInfo API → get full user data
 * 4. Pass user data to Angular Bridge → handle navigation
 */

import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { authAPI } from '../../services/auth-api.service';
import { FormField } from '../ui/FormField';
import { Button } from '../ui/Button';
import type { LoginFormData, LoginFormErrors } from '../../types/auth';
import { useAppDispatch } from '../../hooks/redux';
import { storeCredentialsForMFA, setAuthStep } from '../../store/authSlice';
import { useAuth } from '../../hooks/useAuth';

interface LoginFormProps {
  onSuccess?: (user: any, token: string) => void;
  onError?: (error: string) => void;
  onForgotPassword?: () => void;
  showRememberMe?: boolean;
  onShowToast?: (message: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onError,
  onForgotPassword,
  showRememberMe = true,
  onShowToast,
}) => {
  const dispatch = useAppDispatch();
  const { goToSignup } = useAuth();

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ========================================
  // Validation Functions (matching Angular)
  // ========================================

  const validateEmail = (email: string): string | undefined => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (password.length > 30) return 'Password must be no more than 30 characters';
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: LoginFormErrors = {};

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ========================================
  // Login Handler (Direct API Calls)
  // ========================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setError(null);

    // Validate form
    if (!validateForm()) {
      console.log('[LoginForm] Validation failed:', errors);
      return;
    }

    if (isAccountLocked) {
      setError('Account is locked due to too many failed attempts');
      onError?.('Account is locked due to too many failed attempts');
      return;
    }

    setIsLoading(true);

    try {
      console.log('==========================================');
      console.log('[LoginForm] Step 1: Calling login API...');
      console.log('[LoginForm] Email:', formData.email);
      console.log('[LoginForm] Has Password:', !!formData.password);

      // STEP 1: Call login API (React direct call)
      const loginResponse = await authAPI.login({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe,
      });

      console.log('[LoginForm] Login API response status:', loginResponse.data.status);

      // STEP 2: Extract and store password expiry flag (matching Angular logic)
      const isPasswordExpired = loginResponse.data.data?.isPasswordExpired || false;

      // Store password expiry in sessionStorage for Angular Bridge
      sessionStorage.setItem('passwordExpiry', isPasswordExpired.toString());

      // STEP 3: Check if MFA is required (matching Angular logic)
      const multiAuthEnabled = sessionStorage.getItem('multiAuthentication') === 'true';
      const hasReferenceId = !!loginResponse.data.data?.reference_id;

      if (multiAuthEnabled && hasReferenceId) {
        console.log('[LoginForm] MFA required - reference_id:', loginResponse.data.data.reference_id);
        console.log('[LoginForm] Transitioning to MFA form...');

        // Store credentials in Redux for resend OTP functionality
        dispatch(storeCredentialsForMFA({
          email: formData.email,
          password: formData.password,
          referenceId: loginResponse.data.data.reference_id!,
        }));

        // Show success toast (matching ForgotPasswordForm)
        const successMessage = loginResponse.data.message || 'OTP sent successfully';
        onShowToast?.(successMessage);

        // Trigger transition to MFA step via global event
        if ((window as any).ReactApp?.goToStep) {
          (window as any).ReactApp.goToStep('mfa');
        }

        // Also dispatch event for Angular Bridge to be aware
        window.dispatchEvent(new CustomEvent('react-mfa-required', {
          detail: {
            email: formData.email,
            reference_id: loginResponse.data.data.reference_id,
            credentials: { email: formData.email, password: formData.password }
          }
        }));

        setIsLoading(false);
        return;
      }

      // STEP 4: Regular login flow (no MFA)
      const token = loginResponse.data.data?.token;
      if (!token || token.trim().length === 0) {
        throw new Error('No token received from login API');
      }

      console.log('[LoginForm] Step 4: Storing token from login response');
      console.log('[LoginForm] Token preview:', token.substring(0, 30) + '...');
      console.log('[LoginForm] Token length:', token.length);

      authAPI.storeAuthToken(token);

      // Verify token was stored
      const storedToken = sessionStorage.getItem('finpro_k_Authorization');
      console.log('[LoginForm] Token stored successfully:', !!storedToken);
      console.log('[LoginForm] Stored token matches:', storedToken === token);

      // STEP 5: Call getUserInfo to check password_changed
      console.log('[LoginForm] Step 5: Calling getUserInfo API to check password status');
      const userInfoResponse = await authAPI.getUserInfo();
      const userInfo = userInfoResponse.data.data;

      console.log('[LoginForm] getUserInfo response:', {
        userName: userInfo.name,
        passwordChanged: userInfo.password_changed,
        email: userInfo.email
      });


      // STEP 6: Check if user needs to change password
      if (!userInfo.password_changed) {
        // First-time login - show React change password form
        console.log('[LoginForm] 🔐 First-time login detected - showing password change form');

        // Dispatch Redux action to change auth step
        dispatch(setAuthStep('change-password'));

        // Store email and reason for password change form
        sessionStorage.setItem('change_password_email', userInfo.email);
        sessionStorage.setItem('change_password_reason', 'first-time');

        setIsLoading(false);
        return;
      }

      // STEP 7: Check if password is expired (matching Angular logic)
      if (isPasswordExpired) {
        // Dispatch Redux action to change auth step
        dispatch(setAuthStep('change-password'));

        // Store email and reason for password change form
        sessionStorage.setItem('change_password_email', userInfo.email);
        sessionStorage.setItem('change_password_reason', 'expired');

        setIsLoading(false);
        return;
      }

      // STEP 8: Regular user - pass to Angular Bridge for navigation
      console.log('[LoginForm] ✅ Existing user - calling Angular Bridge for navigation');

      // Call Angular Bridge with token - it will handle navigation
      if ((window as any).AngularAuthBridge?.handlePostLoginWithToken) {
        console.log('[LoginForm] Calling AngularAuthBridge.handlePostLoginWithToken');
        (window as any).AngularAuthBridge.handlePostLoginWithToken(token);
      } else {
        console.error('[LoginForm] ❌ AngularAuthBridge not found on window object!');
        throw new Error('Navigation service not available. Please refresh the page.');
      }

      // Notify parent component (optional)
      onSuccess?.(null, token);
    } catch (err: any) {
      console.error('[LoginForm] ❌ Login failed:', err);
      console.error('[LoginForm] Error details:', {
        message: err.message,
        status: err.status,
        data: err.data,
      });

      // Clean up any stored tokens/data on error
      authAPI.clearAuthToken();
      sessionStorage.removeItem('passwordExpiry');
      sessionStorage.removeItem('change_password_email');
      sessionStorage.removeItem('change_password_reason');

      // Handle specific error cases - prioritize backend message
      let errorMessage = 'An unexpected error occurred. Please try again.';

      // Check for backend error message in various possible locations
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.data?.message) {
        errorMessage = err.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Check for account locked
      if (errorMessage.toLowerCase().includes('locked') || errorMessage.toLowerCase().includes('too many attempts')) {
        setIsAccountLocked(true);
        errorMessage = 'Your account has been locked due to too many failed login attempts. Please try again later or contact support.';
      }

      // Special handling for navigation errors
      if (errorMessage.includes('Navigation service not available')) {
        errorMessage = 'Unable to complete login. Please refresh the page and try again.';
      }

      setError(errorMessage);
      onError?.(errorMessage);

      console.log('==========================================');
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // Event Handlers
  // ========================================

  const handleForgotPassword = () => {
    console.log('[LoginForm] Forgot password clicked');
    onForgotPassword?.();
  };

  const handleFieldChange = (field: keyof LoginFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear field-specific validation error
    if (errors[field as keyof LoginFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    // Clear global auth error when user starts typing
    if (error) {
      setError(null);
    }

    // Clear account locked state when user changes email (trying different account)
    if (field === 'email' && isAccountLocked) {
      setIsAccountLocked(false);
    }
  };

  // ========================================
  // Render
  // ========================================

  return (
    <div className="w-full h-full flex flex-col min-h-0">
      {/* Login Form - Full Height */}
      <div className="flex-1 flex flex-col min-h-0">
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          {/* Form Fields Section - Top */}
          <div className="space-y-6 mt-8">
            <FormField
              label="EMAIL"
              name="email"
              type="email"
              value={formData.email}
              onChange={(value) => handleFieldChange('email', value)}
              error={errors.email}
              placeholder="Enter your email address"
              autoComplete="email"
              required
            />

            {/* Password Field with Forgot Password Link */}
            <div className="space-y-1">
              <div className="flex justify-between items-center mt-4">
                <label htmlFor="password" className="block uppercase" style={{
                  color: '#0F3340',
                  fontFamily: 'Helvetica Neue, sans-serif',
                  fontWeight: 500,
                  fontSize: '1.125rem',
                  lineHeight: '100%',
                  letterSpacing: '0.02em'
                }}>
                  PASSWORD
                </label>
                <span
                  onClick={handleForgotPassword}
                  className="text-sm font-medium cursor-pointer hover:underline"
                  style={{ color: '#0074A5' }}
                >
                  Forgot Password?
                </span>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  placeholder="Contains at least 1 number and 1 special character"
                  autoComplete="current-password"
                  required
                  maxLength={15}
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault()}
                  onCopy={(e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault()}
                  onCut={(e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault()}
                  className={`
                    w-full pl-3 ${formData.password ? 'pr-12' : 'pr-4'} py-3 rounded-lg transition-colors bg-white
                    placeholder-gray-400 disabled:bg-gray-50 disabled:text-gray-500
                    ${errors.password
                      ? 'focus:outline-none focus:ring-2 focus:ring-red-500'
                      : 'focus:outline-none focus:ring-2 focus:ring-[#1B84C6]'}
                  `}
                  style={{
                    border: errors.password ? '1px solid #fca5a5' : '1px solid #D9F0FF'
                  }}
                />
                {formData.password && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    style={{
                      right: '12px',
                      background: 'none',
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      zIndex: 10
                    }}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                )}
              </div>
              {errors.password && <p className="text-sm" style={{ color: '#CA7373' }}>{errors.password}</p>}
            </div>

            {/* Error Message - Plain Text Below Password Field */}
            {(error || isAccountLocked) && (
              <div
                className="text-sm"
                style={{
                  color: '#CA7373',
                  marginTop: error && (
                    error.includes('1 more login attempt') ||
                    error.toLowerCase().includes('one attempt') ||
                    error.toLowerCase().includes('1 attempt') ||
                    error.toLowerCase().includes('last attempt') ||
                    error.toLowerCase().includes('attempt remaining') ||
                    error.toLowerCase().includes('attempt left')
                  ) ? '0.15rem' : undefined
                }}
              >
                {isAccountLocked ? (
                  'Your account has been locked due to too many failed login attempts. Please try again later or contact support.'
                ) : (
                  <>
                    {error}
                    {/* Show reset password option when one attempt remaining */}
                    {error && (
                      error.includes('1 more login attempt') ||
                      error.toLowerCase().includes('one attempt') ||
                      error.toLowerCase().includes('1 attempt') ||
                      error.toLowerCase().includes('last attempt') ||
                      error.toLowerCase().includes('attempt remaining') ||
                      error.toLowerCase().includes('attempt left')
                    ) && (
                      <>
                        {' '}
                        <span
                          onClick={handleForgotPassword}
                          className="inline-block px-3 py-1.5 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                          style={{
                            color: '#0F3340',
                            border: '1px solid #C1E4FB',
                            backgroundColor: '#E5F5FF'
                          }}
                        >
                          click here
                        </span>
                        {' '}if you want to reset your password
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {showRememberMe && (
              <div className="flex items-center">
                {/* Remember me checkbox commented out matching Angular */}
              </div>
            )}
          </div>

          {/* Spacer - pushes button to bottom */}
          <div className="flex-1"></div>

          {/* Button Section - Absolute Bottom */}
          <div className="pb-4">
            <Button
              type="submit"
              loading={isLoading}
              disabled={isAccountLocked || !formData.email || !formData.password}
              fullWidth
              size="lg"
              variant="gradient"
            >
              Login
            </Button>

            {/* Additional Links - matching Angular */}
            <div className="text-center text-sm" style={{ marginTop: '1rem' }}>
              <div className="">
                <span className="font-medium" style={{ color: '#0F3340' }}>New to FinPro?</span>{' '}
                <span
                  onClick={goToSignup}
                  className="font-medium cursor-pointer hover:underline"
                  style={{ color: '#0074A5' }}
                >
                  Create Account
                </span>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
